/**
 * O vínculo de UMA campanha da Meta com o bloco de grupos — aberto a partir da tela Meta Ads.
 *
 * Nasceu de uma pergunta do dono (09/10): "não temos um botão de desvincular as campanhas lá na
 * campanha do Meta?". Tinha — mas só pelo caminho do BLOCO (Campanhas › ⋯ › Meta Ads). Vínculo
 * é uma relação com duas pontas, e cada ponta precisa do seu botão: quem está olhando o gasto
 * de uma campanha da Meta e percebe que ela está no bloco errado não deveria ter que lembrar
 * de qual bloco sair para desfazer.
 *
 * Três ações, todas escritas com o nome do bloco no botão (quem toca sabe o que vai mudar):
 *  - **Mover** — troca de bloco numa escrita só no servidor (`moveMeta`). Desvincular e
 *    vincular em dois passos deixaria uma janela com o gasto fora de todo CPL.
 *  - **Desvincular** — o gasto sai do custo por entrada do bloco; os cliques continuam gravados.
 *  - **Vincular** — para a campanha que ainda não tem bloco.
 */
import { useEffect, useState } from 'react';
import { Button, Dialog, FormAlert, NativeSelect, useToast } from '@mgdlead/ui';

import {
  useCampaigns,
  useLinkCampaignMeta,
  useMoveCampaignMeta,
  useUnlinkCampaignMeta,
} from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';

export type CampanhaDaMeta = {
  externalId: string;
  name: string | null;
  linkedTo: { campaignId: string; name: string } | null;
};

export function VinculoDaCampanha({
  workspaceId,
  campanha,
  onFechar,
}: {
  workspaceId: string;
  campanha: CampanhaDaMeta | null;
  onFechar: () => void;
}) {
  const { toast } = useToast();
  const blocos = useCampaigns(workspaceId).data?.campaigns ?? [];
  const vincular = useLinkCampaignMeta();
  const mover = useMoveCampaignMeta();
  const desvincular = useUnlinkCampaignMeta();
  const ocupado = vincular.isPending || mover.isPending || desvincular.isPending;

  const atual = campanha?.linkedTo ?? null;
  const [destino, setDestino] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    setDestino('');
    setErro(null);
  }, [campanha?.externalId]);

  if (campanha === null) return null;
  const nome = campanha.name ?? `Campanha ${campanha.externalId}`;
  // O bloco atual não aparece como destino: "mover para onde já está" não é uma escolha.
  const opcoes = blocos
    .filter((b) => b.id !== atual?.campaignId)
    .map((b) => ({ value: b.id, label: b.name }));
  const nomeDestino = blocos.find((b) => b.id === destino)?.name ?? '';

  const fechar = () => {
    setErro(null);
    onFechar();
  };
  const falhou = (e: unknown) => setErro(humanizeError(e));

  const aoMover = () =>
    mover.mutate(
      { workspaceId, externalId: campanha.externalId, toCampaignId: destino },
      {
        onSuccess: () => {
          toast({
            title: `Movida para “${nomeDestino}”`,
            description: `O gasto de “${nome}” passa a entrar no custo por entrada de “${nomeDestino}”.`,
            tone: 'success',
          });
          fechar();
        },
        onError: falhou,
      },
    );

  const aoVincular = () =>
    vincular.mutate(
      { workspaceId, campaignId: destino, externalIds: [campanha.externalId], source: 'MANUAL' },
      {
        onSuccess: () => {
          toast({ title: `Vinculada a “${nomeDestino}”`, tone: 'success' });
          fechar();
        },
        onError: falhou,
      },
    );

  const aoDesvincular = () => {
    if (atual === null) return;
    desvincular.mutate(
      { workspaceId, campaignId: atual.campaignId, externalId: campanha.externalId },
      {
        onSuccess: () => {
          toast({
            title: `Desvinculada de “${atual.name}”`,
            description:
              'O gasto dela sai do custo por entrada desse bloco. Os cliques continuam gravados.',
            tone: 'success',
          });
          fechar();
        },
        onError: falhou,
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(abrir) => {
        if (!abrir) fechar();
      }}
      size="sm"
      title={nome}
      description={
        atual
          ? `Vinculada ao bloco “${atual.name}”. O gasto dela entra no custo por entrada desse bloco.`
          : 'Sem bloco: o gasto dela não entra no custo por entrada de nenhum grupo.'
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">
            {atual ? 'Mover para outro bloco' : 'Vincular a um bloco'}
          </span>
          <NativeSelect
            aria-label={atual ? 'Bloco de destino' : 'Bloco para vincular'}
            value={destino}
            onChange={(e) => setDestino(e.target.value)}
            disabled={ocupado}
            options={[{ value: '', label: 'Escolha o bloco…' }, ...opcoes]}
          />
          <Button
            variant="primary"
            block
            disabled={destino === '' || ocupado}
            loading={mover.isPending || vincular.isPending}
            onClick={atual ? aoMover : aoVincular}
          >
            {destino === ''
              ? 'Escolha o bloco'
              : atual
                ? `Mover para “${nomeDestino}”`
                : `Vincular a “${nomeDestino}”`}
          </Button>
        </div>

        {atual ? (
          <Button
            variant="ghost"
            block
            disabled={ocupado}
            loading={desvincular.isPending}
            onClick={aoDesvincular}
          >
            Desvincular de “{atual.name}”
          </Button>
        ) : null}

        {erro !== null ? <FormAlert>{erro}</FormAlert> : null}
      </div>
    </Dialog>
  );
}
